import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { Tag } from '@/components/tag';
import { AppDispatch } from '@/root/store';
import { ShowEvent } from '@/services/events';
import { AbsenTicket, GetGuests, RefundTicket } from '@/services/guests';
import {
  ArrowUturnLeftIcon,
  DocumentCheckIcon,
  NoSymbolIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { EllipsisHorizontalIcon, QrCodeIcon } from '@heroicons/react/24/solid';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

interface Attendee {
  id: number;
  booking_reference: string;
  number: string;
  name: string;
  email: string;
  phone: string;
  phone_url: string;
  date: Date;
  event_name: string;
  ticket_name: string;
  studio_name: string | null;
  time: string | null;
  present_status: {
    code: string;
    label: string;
    color: 'success' | 'warning' | 'danger';
  };
  scans: string;
  scanned_at?: Date;
  first_scanned_at?: Date;
  additional_data: [];
}

export function OrgEventDetailAttendee() {
  const location = useLocation();
  const { slug } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [showAbsen, setShowAbsen] = useState({ modal: false, id: 0 });
  const [showRefund, setShowRefund] = useState({ modal: false, id: 0 });

  const { guests, guest, paginator, exportRoute } = useSelector(
    (state: any) => state.guests
  );
  const { event } = useSelector((state: any) => state.event);

  const absen = (id: number) => {
    dispatch(AbsenTicket(event.id, id));

    setShowAbsen({ modal: false, id: 0 });
  };

  const refund = (id: number) => {
    dispatch(RefundTicket(event.id, id));

    setShowRefund({ modal: false, id: 0 });
  };

  const baseColumns: ColumnDef<Attendee>[] = [
    {
      accessorKey: 'number',
      header: 'No. Tiket',
      cell: ({ row }) => {
        const ticketId = row.original.number;

        return (
          <>
            <div>{ticketId}</div>
          </>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Peserta',
      cell: ({ row }) => {
        const name = row.original.name;
        const email = row.original.email;
        const phone = row.original.phone;
        const phone_url = row.original.phone_url;

        return (
          <>
            <div>{name}</div>
            <div className='text-neutral-70'>{email}</div>
            <a
              className='text-blue-500 underline'
              target='_blank'
              href={phone_url}
            >
              {phone}
            </a>
          </>
        );
      },
    },
    {
      accessorKey: 'date',
      header: 'Tanggal beli tiket',
      cell: ({ row }) => {
        const purchaseDate = row.original.date;
        return (
          <>
            <span>{moment(purchaseDate).format('llll')}</span>
          </>
        );
      },
    },
    {
      accessorKey: 'event_name',
      header: 'Tiket',
      cell: ({ row }) => {
        return (
          <>
            <span className='text-info'>{row.original.ticket_name}</span>
          </>
        );
      },
    },
  ];

  const cinemaColumns: ColumnDef<Attendee>[] = [
    {
      accessorKey: 'studio',
      header: 'Studio',
      cell: ({ row }) => {
        const ticketId = row.original.studio_name;

        return (
          <>
            <div>{ticketId}</div>
          </>
        );
      },
    },
    {
      accessorKey: 'showtime',
      header: 'Jam Tayang',
      cell: ({ row }) => {
        const ticketId = row.original.time;

        return (
          <>
            <div>{ticketId}</div>
          </>
        );
      },
    },
  ];

  // Dynamic columns from event.customer_data
  const dynamicColumns: ColumnDef<Attendee>[] = event?.customer_data
    ? event.customer_data
        .filter((customerField: any) => {
          const fieldName =
            customerField.key || customerField.name || customerField.id;
          const isExcluded = ['name', 'phone', 'email'].includes(
            fieldName?.toLowerCase()
          );
          return !isExcluded && customerField.checked === true;
        })
        .map((customerField: any) => ({
          accessorKey:
            customerField.key || customerField.name || customerField.id,
          header:
            customerField.label || customerField.name || customerField.title,
          /** @ts-ignore */
          cell: ({ row }) => {
            const fieldKey =
              customerField.key || customerField.name || customerField.id;
            const value = row.original?.additional_data?.[fieldKey];

            return (
              <>
                <span>{value || '-'}</span>
              </>
            );
          },
        }))
    : [];

  // Action column
  const actionColumn: ColumnDef<Attendee>[] = [
    {
      accessorKey: 'present_status',
      header: 'Status',
      cell: ({ row }) => {
        const attended = row.original.present_status;
        return <Tag color={attended.color}>{attended.label}</Tag>;
      },
    },
    {
      accessorKey: 'scans',
      header: 'Riwayat Scan',
      cell: ({ row }) => {
        return <>{row.original.scans}x</>;
      },
    },
    {
      accessorKey: 'first_scanned_at',
      header: 'Pertama Kali Discan',
      cell: ({ row }) => {
        return (
          <>
            {row.original.first_scanned_at
              ? moment(row.original.first_scanned_at).format('lll')
              : '-'}
          </>
        );
      },
    },
    {
      accessorKey: 'scanned_at',
      header: 'Terakhir Kali Discan',
      cell: ({ row }) => {
        return (
          <>
            {row.original.scanned_at
              ? moment(row.original.scanned_at).format('lll')
              : '-'}
          </>
        );
      },
    },
    {
      header: 'Aksi',
      cell: ({ row }) => {
        return row.original.present_status.code === 'active' ? (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                size='sm'
                variant='neutral'
                leftIcon={<EllipsisHorizontalIcon />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                className={'cursor-pointer'}
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_CUSTOMER_URL}/tickets/${row.original.booking_reference}`,
                    '_blank'
                  );
                }}
              >
                <QrCodeIcon className='mr-2 h-4 w-4 text-success' />
                <span className='text-success'>Lihat Tiket</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setShowAbsen({ modal: true, id: row.original.id });
                }}
              >
                <DocumentCheckIcon className='mr-2 h-4 w-4 text-success' />
                <span className='text-success'>Sudah Absen</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setShowRefund({ modal: true, id: row.original.id });
                }}
              >
                <ArrowUturnLeftIcon className='mr-2 h-4 w-4 text-danger' />
                <span className='text-danger'>Refund Tiket</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null;
      },
    },
  ];

  // Combine all columns
  const columns = [...baseColumns];

  if (event.type === 'cinema') {
    columns.push(...cinemaColumns);
  }

  columns.push(...dynamicColumns);
  columns.push(...actionColumn);

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    dispatch(GetGuests(slug, searchQuery, page));
  }, [slug, searchQuery, page, guest]);

  return (
    <>
      <Modal
        show={showAbsen.modal}
        onRequestClose={() => setShowAbsen({ ...showAbsen, modal: false })}
        width='medium'
        title='Absen Manual'
        body={
          <>
            <p>Apakah kamu yakin ingin absen peserta ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowAbsen({ ...showAbsen, modal: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'success'}
              onClick={() => {
                absen(showAbsen.id);
              }}
              leftIcon={<TrashIcon />}
            >
              Ya, Absen
            </Button>
          </div>
        }
      />

      <Modal
        show={showRefund.modal}
        onRequestClose={() => setShowRefund({ ...showRefund, modal: false })}
        width='medium'
        title='Refund Tiket'
        body={
          <>
            <p>Apakah kamu yakin ingin refund tiket peserta ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowRefund({ ...showRefund, modal: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'danger'}
              onClick={() => {
                refund(showRefund.id);
              }}
              leftIcon={<TrashIcon />}
            >
              Ya, Refund
            </Button>
          </div>
        }
      />

      <DataTable
        name='Daftar Peserta'
        noun='peserta'
        columns={columns}
        ctaButton={
          <Button element={'link'} to={'qrcode'} leftIcon={<QrCodeIcon />}>
            Scan QR Tiket
          </Button>
        }
        data={guests}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        exportRoute={exportRoute}
        onPageRequest={(nextPage) => setPage(nextPage)}
      />
    </>
  );
}
